import type { Filter } from './types';

// Maps our filter operators to Pinecone query operators
const operatorMap: Record<string, string> = {
  contains: '$in',
  not_contains: '$nin',
  equals: '$eq',
  not_equals: '$ne',
  greater_than: '$gt',
  less_than: '$lt',
};

// Converts a filter value to the appropriate type (number, boolean, or string)
function parseFilterValue(value: string): string | number | boolean {
  const trimmed = value.trim();

  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;

  const numValue = Number(trimmed);
  if (!isNaN(numValue) && trimmed !== '') {
    return numValue;
  }

  return trimmed;
}

// Transforms an array of filters into Pinecone's filter format
export function transformFiltersToPineconeQuery(
  filters: Filter[],
): Record<string, any> | null {
  if (!filters || filters.length === 0) {
    return null;
  }

  const conditions = filters
    .filter(filter => filter.value && filter.value.trim() !== '')
    .map(filter => {
      const pineconeOperator = operatorMap[filter.operator];
      if (!pineconeOperator) {
        return null;
      }

      const parsedValue = parseFilterValue(filter.value);
      const condition: Record<string, any> = {};

      if (filter.operator === 'equals') {
        condition[filter.label] = parsedValue;
      } else if (
        filter.operator === 'contains' ||
        filter.operator === 'not_contains'
      ) {
        condition[filter.label] = {
          [pineconeOperator]: [parsedValue],
        };
      } else {
        condition[filter.label] = {
          [pineconeOperator]: parsedValue,
        };
      }

      return condition;
    })
    .filter(
      (condition): condition is Record<string, any> => condition !== null,
    );

  if (conditions.length === 0) {
    return null;
  }

  if (conditions.length === 1) {
    return conditions[0];
  }

  return {
    $and: conditions,
  };
}
